import React, {useCallback, useState} from 'react';
import {
  FlatList,
  Text,
  View,
  ActivityIndicator,
  Linking,
  TouchableOpacity,
  LayoutAnimation,
  TextInput,
  SectionList,
} from 'react-native';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import Header from '../../components/Header/Header';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import TemporaryPasswordBanner from '../../components/TemporaryPasswordBanner/TemporaryPasswordBanner';
import {styles} from './styles';
import ModalFilter from '../Home/components/ModalFilter/ModalFilter';
import DebtsService from '../../services/DebtsService/DebtsService';
import {useUserStore} from '../../store/UserStore';
import {IDebtsInfo} from '../../types/Debts';
import {Colors} from '../../constants/Colors';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import { useLoadingStore } from '../../store/LoadingStore';

const getStatusInfo = (status: string) => {
  switch (status) {
    case 'succeeded':
      return {label: 'Pago', color: 'green'};
    case 'charged_back':
    case 'refunded':
      return {label: 'Ressarcido', color: 'green'};
    case 'failed':
      return {label: 'Falhou', color: 'red'};
    case 'canceled':
      return {label: 'Pagamento cancelado', color: 'red'};
    case 'pre_authorized':
      return {label: 'Pré-autorizado', color: 'orange'};
    case 'reversed':
      return {label: 'Estornado', color: 'orange'};
    case 'pending':
      return {label: 'Processando pagamento', color: 'orange'};
    case 'new':
      return {label: 'Novo', color: 'orange'};
    case 'partial_refunded':
      return {label: 'Reembolso parcial', color: 'orange'};
    case 'dispute':
      return {label: 'Em disputa', color: 'orange'};
    case 'unpaid':
      return {label: 'Não pago', color: 'orange'};

    default:
      return {label: 'Desconhecido', color: 'gray'};
  }
};

const Debts = () => {
  const {goBack, navigate} = useNavigation<any>();
  const {user} = useUserStore();
  const temporaryPasswordLogin = useUserStore(state => state.temporaryPasswordLogin);
  const [allDebts, setAllDebts] = useState<IDebtsInfo[]>([]);
  const [selectedDebts, setSelectedDebts] = useState<IDebtsInfo[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [errorDebts, setErrorDebts] = useState<boolean>(false);
  const [messageInfo, setMessageInfo] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const { showLoading, hideLoading } = useLoadingStore();

  // Função auxiliar para verificar se um valor é numérico válido
  const isValidNumericValue = (value: any): boolean => {
    return value !== null && value !== undefined && !isNaN(Number(value));
  };

  // Função para calcular o total de um débito
  const calculateDebtTotal = (debt: IDebtsInfo): number => {
    let total = Number(debt.docValue) || 0;

    if (isValidNumericValue(debt.multa)) {
      total += Number(debt.multa);
    }

    if (isValidNumericValue(debt.juros)) {
      total += Number(debt.juros);
    }

    return total;
  };

  const formatData = (dataString:any) => {
    if (!dataString) return '---';

    // Separa a data e cria como data LOCAL (não UTC)
    const [ano, mes, dia] = dataString.split('-');
    const data = new Date(ano, mes - 1, dia);

    return data.toLocaleDateString('pt-BR');
  };

  // Função para remover acentos
  const removeAccents = (str: string) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  };

  // Função para filtrar débitos baseado no texto de busca
  const filterDebts = (debts: IDebtsInfo[], search: string) => {
    if (!search.trim()) return debts;

    const searchNormalized = removeAccents(search.toLowerCase());
    return debts.filter(debt => {
      const order = removeAccents((debt.order || '').toLowerCase());
      const status = removeAccents(
        getStatusInfo(debt.status).label.toLowerCase(),
      );

      return (
        order.includes(searchNormalized) || status.includes(searchNormalized)
      );
    });
  };

  // Função para organizar débitos em categorias
  const organizeDebts = (debts: IDebtsInfo[]) => {
    const filteredDebts = filterDebts(debts, searchText);

    // Débitos que podem ser selecionados (unpaid)
    const selectableStatuses = ['unpaid', 'canceled', 'failed'];
    const selectableDebts = filteredDebts.filter(debt =>
      selectableStatuses.includes(debt.status),
    );

    // Débitos pendentes (status laranja exceto unpaid)
    const pendingStatuses = ['pending', 'pre_authorized', 'new', 'dispute'];
    const pendingDebts = filteredDebts.filter(debt =>
      pendingStatuses.includes(debt.status),
    );

    // Débitos finalizados (não podem ser clicados e não são pendentes)
    const finalizedDebts = filteredDebts.filter(
      debt =>
        !pendingStatuses.includes(debt.status) &&
        !selectableStatuses.includes(debt.status),
    );

    const sections = [];

    // Adiciona seção de débitos selecionáveis
    if (selectableDebts.length > 0) {
      sections.push({
        title: 'Débitos em Aberto',
        subtitle: 'Selecione os débitos para pagamento',
        data: selectableDebts,
        selectable: true,
      });
    }

    // Adiciona seção de débitos pendentes (só se houver itens)
    if (pendingDebts.length > 0) {
      sections.push({
        title: 'Débitos Pendentes',
        subtitle: 'Aguardando processamento',
        data: pendingDebts,
        selectable: false,
      });
    }

    // Adiciona seção de débitos finalizados
    if (finalizedDebts.length > 0) {
      sections.push({
        title: 'Histórico de Débitos',
        subtitle: 'Débitos finalizados',
        data: finalizedDebts,
        selectable: false,
      });
    }

    return sections;
  };

  const GetDebts = useCallback(async () => {
    try {
      setLoading(true);
      setErrorDebts(false);
      setSelectedDebts([]);
      setAllDebts([]);
      setMessageInfo('');

      const response = await DebtsService.GetUpdatedDebts(user?.clientCode);

      if (!response || !Array.isArray(response)) {
        setErrorDebts(true);
        return;
      }

      const isMessageOnly = response.length === 1 && response[0]?.TYPE === 'E';

      if (isMessageOnly) {
        setMessageInfo(
          response[0]?.MESSAGE || 'Cliente não possui débitos em aberto.',
        );
        return;
      }

      const parsedDebts = response.map(item => ({
        id: item.id,
        reference_id: item.BELNR,
        order: item.ZUONR,
        name: item.XBLNR,
        docValue: item.WRBTR,
        ticketExpiration: item.ZFBDT,
        multa: item.MULTA,
        juros: item.JUROS,
        status: item.status,
        docYear: item.GJAHR,
        branch: `00${item.BUZEI}`,
        barCode: item.CODSAC ?? item.BARRAS ?? item.barCode ?? null,
        pixQrCode: item.PIX_QRCODE ?? null,
        chaveNf: item.CHVNF ?? item.CHVNFE ?? null,
      }));

      setAllDebts(parsedDebts);
    } catch (error) {
      console.log(error);
      setErrorDebts(true);
    } finally {
      setLoading(false);
    }
  }, [user?.clientCode]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await GetDebts();
    setRefreshing(false);
  };

  const toggleSelectDebt = (debt: IDebtsInfo) => {
    const exists = selectedDebts.some(d => d.id === debt.id);
    if (exists) {
      setSelectedDebts(prev => prev.filter(d => d.id !== debt.id));
    } else {
      setSelectedDebts(prev => [...prev, debt]);
    }
  };

  const toggleExpand = (id: string) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setExpanded(prev => (prev === id ? null : id));
  };

  function formatCurrency(value: number): string {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    });
  }

  const renderSectionHeader = ({section}: any) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{section.title}</Text>
      <Text style={styles.sectionSubtitle}>{section.subtitle}</Text>
    </View>
  );

  const renderItem = ({item, section}: {item: IDebtsInfo; section: any}) => {
    const isSelected = selectedDebts.some(d => d.id === item.id);
    const isExpanded = expanded === item.id;
    const isSelectable = section.selectable;

    const {label, color} = getStatusInfo(item.status);
    const totalValue = calculateDebtTotal(item);

    return (
      <View>
        <TouchableOpacity
          style={[styles.listItem, isSelected && styles.listItemSelected]}
          onPress={() => {
            if (isSelectable) {
              toggleSelectDebt(item);
            }
          }}
          activeOpacity={isSelectable ? 0.7 : 1}>
          {isSelectable && (
            <View style={isSelected ? styles.checkboxFull : styles.checkbox}>
              <Text style={styles.checkboxText}>{isSelected ? '✔' : ''}</Text>
            </View>
          )}
          <View style={{flex: 1}}>
            <Text style={styles.listItemText}>Fatura: {item.name}</Text>
            <Text style={styles.listItemSub1}>
              Valor: {formatCurrency(totalValue)}
            </Text>
            <Text style={styles.listItemSub}>
              Vencimento: {formatData(item.ticketExpiration) ?? '---'}
            </Text>
            <Text style={styles.listItemSub}>
              Status: <Text style={{color, fontWeight: 'bold'}}>{label}</Text>
            </Text>
          </View>
          <TouchableOpacity onPress={() => toggleExpand(item.id)}>
            {isExpanded ? (
              <FontAwesome style={styles.arrows} name="angle-down" size={24} />
            ) : (
              <FontAwesome style={styles.arrows} name="angle-up" size={24} />
            )}
          </TouchableOpacity>
        </TouchableOpacity>

        {isExpanded && (
          <View style={styles.summaryBox}>
            <View style={styles.summaryLine}>
              <Text style={styles.label}>Subtotal:</Text>
              <Text style={styles.value}>{formatCurrency(item.docValue)}</Text>
            </View>
            {isValidNumericValue(item.multa) && (
              <View style={styles.summaryLine}>
                <Text style={styles.label}>Multa:</Text>
                <Text style={styles.value}>
                  {formatCurrency(Number(item.multa))}
                </Text>
              </View>
            )}
            {isValidNumericValue(item.juros) && (
              <View style={styles.summaryLine}>
                <Text style={styles.label}>Juros:</Text>
                <Text style={styles.value}>
                  {formatCurrency(Number(item.juros))}
                </Text>
              </View>
            )}
            <View style={styles.summaryLine}>
              <Text style={styles.label}>Total:</Text>
              <Text style={styles.value}>{formatCurrency(totalValue)}</Text>
            </View>
          </View>
        )}
      </View>
    );
  };

  useFocusEffect(
    useCallback(() => {
      GetDebts();
    }, [GetDebts]),
  );

  const getTotalValue = () => {
    const total = selectedDebts.reduce((acc, item) => {
      return acc + calculateDebtTotal(item);
    }, 0);

    return total.toFixed(2).replace('.', ',');
  };

  const debtsData = organizeDebts(allDebts);

  return (
    <View style={styles.container}>
      <Header />

      <SectionList
        sections={debtsData}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        keyExtractor={(item, index) => 'key' + index}
        showsVerticalScrollIndicator={false}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        ListEmptyComponent={
          loading ? (
            <ActivityIndicator color={Colors.RedPallete.red} size={20} />
          ) : messageInfo ? (
            <Text style={styles.message}>{messageInfo}</Text>
          ) : (
            <Text style={styles.message}>
              {errorDebts
                ? 'Erro ao carregar os débitos, tente novamente mais tarde.'
                : searchText
                ? 'Nenhum débito encontrado para esta busca.'
                : 'Nenhum débito encontrado.'}
            </Text>
          )
        }
        ListHeaderComponent={
          <View style={styles.spaceHeader}>
            <SearchHeader onPress={goBack} title="Meus Débitos" mb={20} />
            <TemporaryPasswordBanner />
            <Text style={styles.paymentText}>
              Faça o pagamento no prazo e evite bloqueio.
            </Text>

            {/* Campo de busca/filtro */}
            <View style={styles.searchContainer}>
              <FontAwesome
                name="search"
                size={16}
                color={Colors.RedPallete.red}
                style={styles.searchIcon}
              />
              <TextInput
                style={styles.searchInput}
                placeholder="Buscar por ordem ou status..."
                placeholderTextColor="#999"
                value={searchText}
                onChangeText={setSearchText}
              />
              {searchText.length > 0 && (
                <TouchableOpacity
                  onPress={() => setSearchText('')}
                  style={styles.clearButton}>
                  <FontAwesome name="times" size={16} color="#999" />
                </TouchableOpacity>
              )}
            </View>
          </View>
        }
      />

      {selectedDebts.length === 0 && (
        <View style={styles.containerQuestions}>
          <Text style={styles.message}>Tem dúvidas sobre seus débitos?</Text>
          <Text style={styles.paragraph}>
            Entre em contato com nossa equipe de cobrança!
          </Text>
          <Text
            style={styles.phone}
            onPress={() => Linking.openURL('tel:(61) 3356-9858')}>
            (61) 3356-9858
          </Text>
          <Text style={styles.paragraph}>Serviço de Cobranças</Text>
        </View>
      )}

      {selectedDebts.length > 0 && (
        <View style={styles.spacer}>
        </View>
      )}

      {selectedDebts.length > 0 && (
        <View style={styles.buttonWrapper}>
          <View style={styles.buttonBackground} />
          <View style={styles.totalRow}>
            <Text style={styles.totalText}>
              R$ {getTotalValue()} / {selectedDebts.length} débitos
            </Text>
            <ButtonComponent
              title="Continuar"
              disabled={temporaryPasswordLogin}
              onPress={() => {
                showLoading();
                navigate('DebtsPayment', {items: selectedDebts})
              }}
              width="40%"
            />
          </View>
        </View>
      )}
    </View>
  );
};

export default Debts;
